import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SelectField from '../fields/SelectField';
import NumberField from '../fields/NumberField';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import TextField from '../fields/TextField';
import BidsPlaceBidButton from './BidsPlaceBidButton';
import useMultiValidator from '../../hooks/useMultiValidator';
import { membersValidator } from '../../validators/members';
import { usersValidator } from '../../validators/users';
import { quantityValidator } from '../../validators/quantity';
import { priceValidator } from '../../validators/price';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_BIDS_QUERY_NAME, SUBMIT_BID } from '../../graphql/bids';
import useAuthentication from '../../hooks/useAuthentication';
import { getUserIdFromToken } from '../../utils/authentication';
import { auctionMembersType, auctionType } from '../../types';
import './_styles_/BidsPlaceBid.css';
import useExtendedState from '../../hooks/useExtendedState';
import {
    getProductType,
    SECURITY,
} from '../../utils/ProductTypes';
import { getBiddingUserConfiguration } from '../../reducers';
import WarningPanel from '../WarningPanel';
import useSingleValidator from '../../hooks/useSingleValidator';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

BidsPlaceBidOpen.propTypes = {
    auction: auctionType.isRequired,
    auctionMembers: auctionMembersType.isRequired,
    isQuantityStepValid: PropTypes.bool.isRequired,
    quantityStep: PropTypes.number.isRequired,
    isPriceStepValid: PropTypes.bool.isRequired,
    priceStep: PropTypes.number.isRequired,
    onBidAdded: PropTypes.func.isRequired,
};

function BidsPlaceBidOpen(props) { // NOSONAR javascript:S3776
    const {
        auction,
        auctionMembers,
        isQuantityStepValid,
        quantityStep,
        isPriceStepValid,
        priceStep,
        onBidAdded,
    } = props;
    const { t } = useTranslation();
    const productType = getProductType(auction.product.productType);
    const { userPrivileges, accessToken } = useAuthentication();
    const currentUserRacf = getUserIdFromToken(accessToken);
    const hasMoreMembers = auctionMembers.length > 1;
    const biddingUserConfiguration = useSelector(getBiddingUserConfiguration);
    let isConflictWithBiddingUserConfigurationDefaultQuantity = false;
    let initialQuantity = auction.smallestUnit;
    if (biddingUserConfiguration.defaultQuantity !== null) {
        if (biddingUserConfiguration.defaultQuantity >= auction.smallestUnit
            && biddingUserConfiguration.defaultQuantity <= (auction.maxBidSize || auction.quantity)
            && (biddingUserConfiguration.defaultQuantity % auction.smallestUnit) === 0) {
            initialQuantity = biddingUserConfiguration.defaultQuantity;
        } else {
            isConflictWithBiddingUserConfigurationDefaultQuantity = true;
        }
    }
    const quantityState = useExtendedState(initialQuantity);
    const minQuantityState = useExtendedState(auction.smallestUnit);
    const maxQuantityState = useExtendedState(auction.maxBidSize || auction.quantity);
    let isConflictWithBiddingUserConfigurationDefaultPrice = false;
    let initialPrice = auction.minPrice;
    if (biddingUserConfiguration.defaultPrice !== null) {
        if ((!auction.minPrice || biddingUserConfiguration.defaultPrice >= auction.minPrice)
            && (!auction.maxPrice || biddingUserConfiguration.defaultPrice <= auction.maxPrice)) {
            initialPrice = biddingUserConfiguration.defaultPrice;
        } else {
            isConflictWithBiddingUserConfigurationDefaultPrice = true;
        }
    }
    const priceState = useExtendedState(initialPrice);
    const asBrokerReferenceState = useExtendedState('');
    const selectedMemberIdState = useExtendedState(hasMoreMembers ? '' : auctionMembers[0].memberId);
    const [users, setUsers] = useState(hasMoreMembers ? [] : auctionMembers[0].users);
    const selectedUserState = useExtendedState('');
    const handleSelectedUserChange = (newValue) => {
        let parsedSelectedUser;
        try {
            parsedSelectedUser = JSON.parse(newValue);
            selectedUserState.setValue(parsedSelectedUser);
        } catch (e) {
            selectedUserState.reset();
        }
    };
    const resetQuantityFieldSetup = useCallback(() => {
        minQuantityState.resetIfNecessary();
        maxQuantityState.resetIfNecessary();
        quantityState.changeInitialState(initialQuantity);
        quantityState.setValueIfNew(initialQuantity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        quantityState.changeInitialState,
        minQuantityState.resetIfNecessary,
        maxQuantityState.resetIfNecessary,
        quantityState.setValueIfNew,
    ]);
    const handleSelectedMemberChange = (newMemberId) => {
        const selectedMember = auctionMembers.find(({ memberId }) => memberId === newMemberId);
        const newUsers = selectedMember ? selectedMember.users : [];
        selectedUserState.reset();
        setUsers(newUsers);
        resetQuantityFieldSetup();
    };
    const canEditAllBids = hasPrivilege(userPrivileges, [Privileges.BID_EDIT_ALL]);
    const { validators, isValid } = useMultiValidator(false, {
        members: membersValidator(t),
        users: usersValidator(t),
        quantity: quantityValidator(t, minQuantityState.value, maxQuantityState.value),  // waf validator not required as the input range is validated by quantity validator
        price: compositeValidator(priceValidator(t, auction.minPrice, auction.maxPrice), wafValidator(t)),
    }, {
        members: !hasMoreMembers,
        users: !canEditAllBids,
        quantity: maxQuantityState.value !== 0,
        price: !!initialPrice,
    });
    const { validator: referenceValidator, isValid: isReferenceValid } = useSingleValidator(true, useMemo(() => wafValidator(t), [t]));
    const initialValidity = maxQuantityState.value === 0 ? Promise.reject() : Promise.resolve();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([initialValidity, isReferenceValid(), isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(SUBMIT_BID, {
        variables: {
            auctionId: auction.id,
            memberId: selectedMemberIdState.value,
            userRacf: selectedUserState.value ? selectedUserState.value.racf : currentUserRacf,
            quantity: quantityState.value,
            price: priceState.value,
            asBrokerReference: asBrokerReferenceState.value,
        },
        successCallback: onBidAdded,
        failureMessageOptions: { key: 'auctions.bids-list.place-bid.notification.place-bid-failure', params: { count: 1 } },
        refetchQueries: [ALL_BIDS_QUERY_NAME],
    });
    return (
        <form
            className="auction-bids--place-bid rich-list-row-expanded"
            onSubmit={(event) => event.preventDefault()}
        >
            <div className="auction-bids--place-bid--owner">
                {hasMoreMembers && (
                    <SelectField
                        className="auction-bids--place-bid--member"
                        label={t('auctions.bids-list.member')}
                        state={selectedMemberIdState}
                        onChange={handleSelectedMemberChange}
                        validator={validators.members}
                        emptyOption={<option value="">...</option>}
                        options={auctionMembers.map(({ memberId, auctionLimit }) => (
                            auctionLimit !== null
                                ? (<option value={memberId} key={memberId}>{memberId} (max: {auctionLimit})</option>)
                                : (<option value={memberId} key={memberId}>{memberId}</option>)
                        ))}
                    />
                )}
                {canEditAllBids && (
                    <SelectField
                        className="auction-bids--place-bid--user"
                        label={t('auctions.bids-list.user')}
                        value={selectedUserState.value === '' ? selectedUserState.value : JSON.stringify(selectedUserState.value)}
                        onChange={handleSelectedUserChange}
                        validator={validators.users}
                        emptyOption={<option value="">...</option>}
                        options={users.map((user) => <option value={JSON.stringify(user)} key={user.userCode}>{user.racf} ({user.name})</option>)}
                        isChanged={selectedUserState.isChanged}
                        onReset={selectedUserState.reset}
                    />
                )}
                <NumberField
                    className="auction-bids--place-bid--quantity"
                    label={t('auctions.bids-list.quantity')}
                    state={quantityState}
                    validator={validators.quantity}
                    step={quantityStep}
                    min={minQuantityState.value}
                    max={maxQuantityState.value}
                    decrementDisabled={!isQuantityStepValid || maxQuantityState.value === 0}
                    incrementDisabled={!isQuantityStepValid || maxQuantityState.value === 0}
                    disabled={maxQuantityState.value === 0}
                />
            </div>
            <div className="auction-bids--place-bid--prices">
                <NumberField
                    className="auction-bids--place-bid--price"
                    label={t('auctions.bids-list.price')}
                    postfix={<>&nbsp;{auction.zone.currency.code}</>}
                    state={priceState}
                    fractionDigits={2}
                    validator={validators.price}
                    valueTransformer={(v) => isNaN(v) ? null : v}
                    step={priceStep}
                    min={auction.minPrice}
                    max={auction.maxPrice}
                    decrementDisabled={!isPriceStepValid}
                    incrementDisabled={!isPriceStepValid}
                />
            </div>
            {productType.is(SECURITY) && hasPrivilege(selectedUserState.value ? selectedUserState.value.permissions : userPrivileges, [Privileges.BID_EDIT_AS_BROKER]) && (
                <TextField
                    className="auction-bids--place-bid--reference"
                    label={t('auctions.bids-list.reference')}
                    state={asBrokerReferenceState}
                    validator={referenceValidator}
                />
            )}
            <div className="auction-bids--place-bid--button">
                <BidsPlaceBidButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    label={t('auctions.bids-list.button.place-bid')}
                />
            </div>
            {(isConflictWithBiddingUserConfigurationDefaultQuantity || isConflictWithBiddingUserConfigurationDefaultPrice) && (
                <>
                    {isConflictWithBiddingUserConfigurationDefaultQuantity && (
                        <WarningPanel message={t('auctions.bids-list.user-configuration-conflict-with-default-quantity')} />
                    )}
                    {isConflictWithBiddingUserConfigurationDefaultPrice && (
                        <WarningPanel message={t('auctions.bids-list.user-configuration-conflict-with-default-price')} />
                    )}
                </>
            )}
        </form>
    );
}

export default BidsPlaceBidOpen;
